import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LayoutDashboard, Plus } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  Modal,
  SurfacePageBody,
  SurfacePageCard,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_PRIMARY, GLASS_CARD_TILE, GLASS_INPUT } from '@/shared/ui-class';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import {
  projectModulesSeed,
  type DashboardPeriod,
  type DashboardStat,
} from '@/versions/v1/shared/project-modules-seed';
import {
  chartGeometry,
  DASHBOARD_PERIOD_LABELS,
  dashboardForPeriod,
} from './ProjectDashboardsScene.transform';

const PERIODS: DashboardPeriod[] = ['7d', '30d', '90d'];

function InlineChart({
  title,
  subtitle,
  series,
  showLine = true,
  minBaselineRatio,
}: {
  title: string;
  subtitle: string;
  series: { bar: number[]; line: number[] };
  showLine?: boolean;
  minBaselineRatio?: number;
}) {
  const geometry = useMemo(
    () => chartGeometry(series, minBaselineRatio != null ? { minBaselineRatio } : undefined),
    [series, minBaselineRatio],
  );

  return (
    <SurfacePageCard className="flex flex-col gap-3 p-5">
      <div>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{title}</h3>
        <p className="mt-0.5 text-xs text-muted">{subtitle}</p>
      </div>
      <svg
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        className="h-32 w-full"
        role="img"
        aria-label={title}
      >
        {geometry.bars.map((bar, index) => (
          <rect
            key={index}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            fill="color-mix(in srgb, var(--color-accent-500) 30%, transparent)"
            rx={2}
          />
        ))}
        {showLine ? (
          <polyline
            points={geometry.linePoints}
            fill="none"
            className="stroke-accent-600 dark:stroke-accent-400"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
      </svg>
    </SurfacePageCard>
  );
}

function nextWidgetId(stats: DashboardStat[]): string {
  const max = stats.reduce((acc, stat) => {
    const n = Number.parseInt(stat.id.replace(/\D/g, ''), 10);
    return Number.isFinite(n) ? Math.max(acc, n) : acc;
  }, 0);
  return `widget-${max + 1}`;
}

export function ProjectDashboardsScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const seed = projectModulesSeed(projectId);

  const [period, setPeriod] = useState<DashboardPeriod>('7d');
  const [extraWidgets, setExtraWidgets] = useState<DashboardStat[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [widgetName, setWidgetName] = useState('');

  useEffect(() => {
    document.title = project
      ? `Dashboards · ${project.name} · Foundation`
      : 'Dashboards · Foundation';
  }, [project]);

  const periodData = useMemo(
    () => dashboardForPeriod(seed.dashboards, period),
    [seed.dashboards, period],
  );

  const visibleStats = useMemo(
    () => [...periodData.stats, ...extraWidgets],
    [periodData.stats, extraWidgets],
  );

  const isEmpty =
    periodData.stats.every((s) => s.value === '0') &&
    periodData.charts.throughput.bar.every((v) => v === 0) &&
    extraWidgets.length === 0;

  const handleAddWidget = () => {
    const name = widgetName.trim();
    if (!name) return;
    setExtraWidgets((prev) => [
      ...prev,
      {
        id: nextWidgetId([...periodData.stats, ...prev]),
        label: name,
        value: '—',
        sub: 'Placeholder widget',
      },
    ]);
    setWidgetName('');
    setShowAddModal(false);
  };

  const openAddModal = () => setShowAddModal(true);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectDashboardsScene">
        <SurfacePageBody width="full" className="space-y-6">
          <SurfacePageHeader
            kicker={project.name}
            title="Dashboards"
            description="Metrics boards and live views built from project data."
            actions={<HelpAnchor doc="workspace/project-dashboards" label="Dashboards" />}
          />

          <div className="flex flex-wrap items-center gap-2">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                className={cn(
                  'inline-flex h-8 items-center rounded-full border px-3 text-xs font-medium transition-colors',
                  period === p
                    ? 'border-accent-500 bg-[color-mix(in_srgb,var(--color-accent-500)_10%,transparent)] text-accent-700 dark:text-accent-300'
                    : 'border-[var(--glass-border)] text-zinc-600 hover:bg-zinc-900/[0.04] dark:text-zinc-400 dark:hover:bg-white/[0.04]',
                )}
                onClick={() => setPeriod(p)}
              >
                {DASHBOARD_PERIOD_LABELS[p]}
              </button>
            ))}
          </div>

          {isEmpty ? (
            <EmptyState
              icon={<LayoutDashboard className="mx-auto h-8 w-8 text-zinc-400" />}
              title="No dashboard data yet"
              description="Widgets will appear here once this project has workflow runs and completed tasks."
              action={
                <button
                  type="button"
                  className="btn-ghost inline-flex h-9 items-center gap-1.5 px-3 text-sm"
                  onClick={openAddModal}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add widget
                </button>
              }
            />
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {visibleStats.map((stat) => (
                  <div key={stat.id} className={cn('flex flex-col p-5', GLASS_CARD_TILE)}>
                    <p className="text-sm font-medium text-muted">{stat.label}</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{stat.sub}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <InlineChart
                  title="Throughput"
                  subtitle={`Runs per day · ${DASHBOARD_PERIOD_LABELS[period]}`}
                  series={periodData.charts.throughput}
                />
                <InlineChart
                  title="Latency"
                  subtitle={`P95 ms · ${DASHBOARD_PERIOD_LABELS[period]}`}
                  series={periodData.charts.latency}
                  showLine={false}
                  minBaselineRatio={0.9}
                />
              </div>

              <button
                type="button"
                className={cn(
                  'flex min-h-[8rem] w-full flex-col items-center justify-center gap-2 rounded-2xl',
                  'border border-dashed border-[var(--glass-border)] text-sm text-muted',
                  'transition-colors hover:border-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)] hover:bg-zinc-900/[0.02] dark:hover:bg-white/[0.02]',
                )}
                onClick={openAddModal}
              >
                <Plus className="h-5 w-5" />
                Add widget
              </button>
            </div>
          )}
        </SurfacePageBody>

        <Modal open={showAddModal} onOpenChange={setShowAddModal} title="Add widget">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="widget-name"
                className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
              >
                Widget name
              </label>
              <input
                id="widget-name"
                type="text"
                value={widgetName}
                onChange={(e) => setWidgetName(e.target.value)}
                placeholder="e.g. Error rate"
                className={cn('h-10 w-full rounded-lg px-3 text-sm', GLASS_INPUT)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-ghost inline-flex h-9 items-center px-3 text-sm"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={cn(BUTTON_PRIMARY, 'inline-flex h-9 items-center px-4 text-sm')}
                disabled={!widgetName.trim()}
                onClick={handleAddWidget}
              >
                Add widget
              </button>
            </div>
          </div>
        </Modal>
      </Commentable>
    </SurfacePageLayout>
  );
}
